/* eslint-disable prefer-arrow/prefer-arrow-functions */
/* eslint-disable @typescript-eslint/naming-convention */
import { EventInterface, PlaylistInterface } from '../playlist/playlist.interface';
import { LocationModel } from '../models/location';
import { cloneDeep } from 'lodash';
import { Action, createReducer, on } from '@ngrx/store';
import * as PlayerActions from './player.actions';

export enum PlayerStatus {
  NotReady = 'Not Ready',
  Refreshing = 'Refreshing',
  Ready = 'Ready',
  PlayingSource = 'Playing source',
  PlayingPrayerTime = 'Playing prayer time',
  PlayingAd = 'Playing Ad',
  NotAutoplayPermission = 'Not Autoplay Permission',
}

export enum LocationStatus {
  Locked = 'Locked',
  Unlocked = 'Unlocked',
}

export interface PlayerState {
  status: PlayerStatus;
  isOnline: boolean;
  isInit: boolean;
  initialTracksLoaded: boolean;
  locationStatus: LocationStatus;
  location: LocationModel;
  currentPlaylist: PlaylistInterface;
  tracks: EventInterface[];
  activeTrack: EventInterface;
  nextTrack: EventInterface;
  forcePlayNextTrack: boolean;
  trackErrors: {
    track: EventInterface;
    error: any;
  }[];
  muted: boolean;
  volume: number;
}

const initialPlayerState: PlayerState = {
  status: PlayerStatus.NotReady,
  isOnline: true,
  isInit: false,
  initialTracksLoaded: false,
  locationStatus: LocationStatus.Unlocked,
  location: null,
  currentPlaylist: null,
  tracks: [],
  activeTrack: null,
  nextTrack: null,
  forcePlayNextTrack: false,
  trackErrors: [],
  muted: false,
  volume: 10,
};

const reducer = createReducer(
  initialPlayerState,
  on(PlayerActions.LocationIsLocked, (state: PlayerState, action) => ({
      ...state,
      locationStatus: LocationStatus.Locked,
    })),
  on(PlayerActions.LocationIsNotLocked, PlayerActions.LocationLockedStatusReset, (state: PlayerState, action) => ({
      ...state,
      locationStatus: LocationStatus.Unlocked,
    })),
  on(PlayerActions.LoadPlaylistFromServer, (state: PlayerState, action) => ({
      ...state,
      tracks: [],
    })),
  on(PlayerActions.LoadPlaylistSuccess, PlayerActions.LoadPlaylistFromLocalStorageSuccess, (state: PlayerState, action) => {
    const currentPlaylist = cloneDeep(action.playlist);
    return {
      ...state,
      currentPlaylist,
    };
  }),
  on(PlayerActions.SetInitialTracks, (state: PlayerState, action) => {
    const tracks = cloneDeep(action.tracks);
    return {
      ...state,
      initialTracksLoaded: true,
      tracks,
    };
  }),
  on(PlayerActions.LoadMoreTracksSuccess, (state: PlayerState, action) => {
    const newTracks = cloneDeep(action.tracks);
    const tracks = [...state.tracks, ...newTracks];
    return {
      ...state,
      initialTracksLoaded: true,
      tracks,
    };
  }),
  on(PlayerActions.LoadNextTrackAndPlay, (state: PlayerState, action) => ({
      ...state,
      forcePlayNextTrack: true,
    })),
  on(PlayerActions.LoadNextTrackFailure, (state: PlayerState, action) => {
    const trackErrors = cloneDeep(state.trackErrors);
    const track = cloneDeep(action.track);
    const error = cloneDeep(action.error);
    trackErrors.push({
      track,
      error,
    });
    return {
      ...state,
      trackErrors,
    };
  }),
  on(PlayerActions.BypassTrackAndLoadNext, (state: PlayerState, action) => {
    const tracks = cloneDeep(state.tracks);
    const trackIndex = tracks.findIndex(track => track.id === action.track.id);
    tracks.splice(trackIndex, 1);
    return {
      ...state,
      tracks,
    };
  }),
  on(PlayerActions.MaxTrackErrorsReachedSuccess, (state: PlayerState, action) => ({
      ...state,
      trackErrors: [],
    })),
  on(PlayerActions.PlayerIsReady, (state: PlayerState, action) => {
    if (state.status === PlayerStatus.Ready) {
      return { ...state };
    }
    const status = state.status === PlayerStatus.NotReady || state.status === PlayerStatus.Refreshing ? PlayerStatus.Ready : state.status;
    return {
      ...state,
      status,
    };
  }),
  on(
    PlayerActions.LoadLocationSuccess,
    PlayerActions.ReloadLocationSuccess,
    PlayerActions.RefreshLocationInformationSuccess,
    (state: PlayerState, action) => {
      const location = cloneDeep(action.location);
      return {
        ...state,
        location,
        volume: location.volume,
      };
    },
  ),
  on(PlayerActions.SetPrayerTimeOn, (state: PlayerState, action) => ({
      ...state,
      status: PlayerStatus.PlayingPrayerTime,
    })),
  on(PlayerActions.SetAdOn, (state: PlayerState, action) => ({
      ...state,
      status: PlayerStatus.PlayingAd,
    })),
  on(PlayerActions.SetPrayerTimeOff, PlayerActions.SetAdOff, (state: PlayerState, action) => ({
      ...state,
      status: state.status === PlayerStatus.NotReady ? PlayerStatus.NotReady : PlayerStatus.Ready,
    })),
  on(PlayerActions.Play, (state: PlayerState, action) => {
    const status =
      state.status === PlayerStatus.NotReady || state.status === PlayerStatus.Ready ? PlayerStatus.PlayingSource : state.status;
    return {
      ...state,
      isInit: true,
      status,
      forcePlayNextTrack: false,
    };
  }),
  on(PlayerActions.PlayNext, (state: PlayerState, action) => {
    const activeTrack = cloneDeep(state.nextTrack);
    return {
      ...state,
      isInit: true,
      activeTrack,
      nextTrack: null,
      forcePlayNextTrack: false,
    };
  }),
  on(PlayerActions.ToggleMute, (state: PlayerState, action) => {
    const muted = state.status === PlayerStatus.PlayingPrayerTime || state.status === PlayerStatus.PlayingAd ? true : !state.muted;
    return {
      ...state,
      muted,
    };
  }),
  on(PlayerActions.Mute, (state: PlayerState, action) => {
    const muted = true;
    return {
      ...state,
      muted,
    };
  }),
  on(PlayerActions.Unmute, (state: PlayerState, action) => {
    const muted = false;
    return {
      ...state,
      muted,
    };
  }),
  on(PlayerActions.ChangeVolume, (state: PlayerState, action) => ({
      ...state,
      volume: action.volume,
    })),
  on(PlayerActions.IsOffline, (state: PlayerState, action) => ({
      ...state,
      isOnline: false,
    })),
  on(PlayerActions.IsOnline, (state: PlayerState, action) => ({
      ...state,
      isOnline: true,
    })),
  on(PlayerActions.ResetAll, (state: PlayerState, action) => ({
      ...initialPlayerState,
    })),
  on(PlayerActions.LoadNextTrackSuccess, (state: PlayerState, action) => {
    const nextTrack = cloneDeep(action.nextTrack);
    let tracks = state.tracks.map(it => cloneDeep(it));
    // If there is no activeTracks we just start the player
    const index = state.activeTrack ? state.tracks.findIndex(track => track.id === state.activeTrack.id) : 0;
    if (index > -1 && state.isOnline) {
      tracks = tracks.splice(index);
    }
    return {
      ...state,
      tracks,
      nextTrack,
    };
  }),
  on(PlayerActions.AutoplayPermissionDenied, (state: PlayerState, action) => {
    const muted = true;
    const status = PlayerStatus.NotAutoplayPermission;
    return {
      ...state,
      muted,
      status,
    };
  }),
  on(PlayerActions.AutoplayPermissionAllowed, (state: PlayerState, action) => {
    const muted = false;
    const status = PlayerStatus.Ready;
    return {
      ...state,
      muted,
      status,
    };
  }),
);

export function playerReducers(state: PlayerState, action: Action) {
  return reducer(state, action);
}
